<script setup lang="ts">
import type { LocationListItem } from "@ca/db/locations";

import { computed, ref } from "vue";

import {
	ScrollAreaCorner, ScrollAreaRoot, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport,
} from "reka-ui";

import { formatDegrees } from "@ca/formatters/degrees";
import { formatDistance } from "@ca/formatters/distance";

import {
	airborneOnly, insuredOnly, searchTerm, 
} from "@/composables/filters";

import AircraftDetailPanel from "./AircraftDetailPanel.vue";

const props = defineProps<{
	locations: LocationListItem[]
}>();

const emit = defineEmits<{
	cardClick: [
		{
			aircraftId: string;
			selected: boolean;
		}
	]
}>();

const filtersOpen = ref(false);
const selected = ref<null | string>(null);

const selectAircraft = (aircraftId: string) => {
	if (selected.value === aircraftId) {
		selected.value = null;
	} else {
		selected.value = aircraftId;
	}
	emit("cardClick", {
		aircraftId,
		selected: selected.value === aircraftId,
	});
};


const sortedLocations = computed(() => {
	const sorted = props.locations.toSorted((a, b) => {
		return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
	});

	return sorted;
});

const formatTime = (timestamp: Date | string) => {
	const date = new Date(timestamp);
	return date.toLocaleString();
};


</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="bg-gray-100/75 rounded-[20px] p-4 w-fit flex items-center gap-2 transition-transform">
      <AircraftCreateModal>
        <UButton
          icon="ic-round-plus"
        />
      </AircraftCreateModal>
    </div>
    <div class="bg-gray-100/75 rounded-[20px] p-4 w-fit flex items-center gap-2 transition-transform">
      <UButton
        icon="ic-round-filter-alt"
        @click="filtersOpen = !filtersOpen"
      />
      <div
        v-if="filtersOpen"
        class="flex items-center gap-2"
      >
        <USeparator
          orientation="vertical"
          class="h-8 mx-2"
          color="secondary"
        />
        <USwitch
          v-model="insuredOnly"
          label="Only show insured"
        />
        <USeparator
          orientation="vertical"
          class="h-8 mx-2"
          color="secondary"
        />
        <USwitch
          v-model="airborneOnly"
          label="Only show airborne"
        />
        <USeparator
          orientation="vertical"
          class="h-8 mx-2"
          color="secondary"
        />
        <UInput
          v-model="searchTerm"
          placeholder="Filter by tail number"
          size="sm"
        />
      </div>
    </div>

    <ScrollAreaRoot
      style="--scrollbar-size: 10px"
      class="relative"
    >
      <ScrollAreaViewport class="flex w-full snap-x snap-mandatory">
        <div class="flex flex-row gap-2">
          <div
            v-for="loc in sortedLocations"
            :key="loc.aircraftId"
            :class="selected === loc.aircraftId ? 'border-brand-500' : 'border-transparent'"
            class="bg-gray-100/75 rounded-[20px] p-4 w-80 cursor-pointer snap-start hover:bg-gray-200/75 border-2 transition-colors"
            @click="selectAircraft(loc.aircraftId)"
          >
            <div class="font-cta text-lg grid grid-cols-3 items-center justify-between">
              <AircraftAvatar
                class="col-span-2"
                :aircraft="{
                  id: loc.aircraftId,
                  tailNumber: loc.tailNumber,
                  make: loc.make,
                  model: loc.model,
                  year: loc.year,
                }"
              />
              
              <div class="flex flex-col gap-1 justify-end">
                <div class="flex items-center gap-1 justify-end">
                  <div class="text-sm text-brand-500 capitalize">
                    {{ loc.flightState }}
                  </div>
                  <div
                    class="flex items-center p-1 rounded-full"
                    :class="loc.flightState === 'airborne' ? 'bg-primary-500' : 'bg-brand-200'"
                  >
                    <UIcon
                      class="size-3"
                      :name="loc.flightState === 'airborne' ? 'ic-round-airplanemode-active' : 'ic-round-airplanemode-inactive'"
                    />
                  </div>
                </div>

                <div class="flex items-center gap-1 justify-end">
                  <span class="text-sm">{{ formatDegrees(loc.heading, 0) }}</span>
                  <div class="flex items-center p-1 rounded-full bg-blue-100">
                    <UIcon
                      class="text-blue-700 size-3"
                      :style="{ transform: `rotate(${(loc.heading ?? 0) - 225}deg)` }"
                      name="mingcute-compass-3-fill"
                    />
                  </div>
                </div>
              </div>
            </div>

            <USeparator
              class="my-2"
              color="secondary"
            />
            <div class="flex flex-row gap-1 justify-between">
              <div class="text-xs text-gray-500 font-sans flex flex-col gap-1.5">
                <div>
                  Speed: {{ loc.speed }} kts
                </div>
                <div>
                  Altitude: {{ formatDistance({ distance: loc.altitude, unit: "foot" }) }}
                </div>
                <div>
                  Updated: {{ formatTime(loc.timestamp) }}
                </div>
              </div>
              <div class="flex flex-col justify-end">
                <AircraftDetailPanel
                  :pac="{
                    id: loc.aircraftId,
                    tailNumber: loc.tailNumber,
                    make: loc.make,
                    model: loc.model,
                    year: loc.year,
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar orientation="horizontal">
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
      <ScrollAreaScrollbar orientation="vertical">
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
      <ScrollAreaCorner />
    </ScrollAreaRoot>
  </div>
</template> 